/**
 * Sync seam — vendor-neutral, local-first.
 *
 * `SyncTarget` is the port a backend implements: Postgres-wire, Supabase, an
 * S3-backed log, or your own VPS. Nothing above this interface knows which
 * backend is in use, so migrating providers is a config/adapter swap — no
 * vendor lock-in. `LoopbackSyncTarget` is the in-memory reference adapter used
 * in tests and for the "try it offline" demo.
 *
 * `SyncEngine` drains the local-first mutation queue against a target. It never
 * silently overwrites: when both sides advanced past the shared base version it
 * records a ConflictReview instead of pushing. Strict-local / device-secret
 * data is terminal and never crosses the seam.
 */

import {
  type EntityId,
  type EntityKind,
  type PrivacyClass,
  type RecordEnvelope,
  isLocalOnly,
} from '@life-os/app-core';
import type { Clock } from './clock.js';
import { type LocalStore, Collections } from './local-store.js';
import {
  type Mutation,
  type ConflictReview,
  type RemoteRecordSnapshot,
  detectConflict,
} from './mutation.js';

/** A record as it crosses the sync boundary. `body` is the opaque envelope record. */
export interface SyncRecord {
  readonly entityId: EntityId;
  readonly kind: EntityKind;
  readonly version: number;
  readonly privacyClass: PrivacyClass;
  readonly updatedAt: string;
  readonly body: unknown;
}

/**
 * The one interface a cloud backend must satisfy. Three operations:
 *  - head:  the remote's current version for an entity (or null if absent)
 *  - push:  upsert a record on the remote
 *  - pull:  records changed since a watermark (null = full pull)
 */
export interface SyncTarget {
  readonly name: string;
  head(entityId: EntityId): Promise<RemoteRecordSnapshot | null>;
  push(record: SyncRecord): Promise<void>;
  pull(since: string | null): Promise<readonly SyncRecord[]>;
  /** Fetch a single remote record by id (used when accepting the remote side of a conflict). */
  get(entityId: EntityId): Promise<SyncRecord | null>;
}

/** In-memory reference target. Deterministic; used in tests and offline demos. */
export class LoopbackSyncTarget implements SyncTarget {
  readonly name = 'loopback';
  private readonly records = new Map<string, SyncRecord>();

  /** Simulate a remote-side write (e.g. another device synced first). */
  seed(record: SyncRecord): void {
    this.records.set(String(record.entityId), record);
  }

  async head(entityId: EntityId): Promise<RemoteRecordSnapshot | null> {
    const r = this.records.get(String(entityId));
    return r ? { entityId, version: r.version } : null;
  }

  async push(record: SyncRecord): Promise<void> {
    this.records.set(String(record.entityId), record);
  }

  async pull(since: string | null): Promise<readonly SyncRecord[]> {
    const all = [...this.records.values()];
    return since === null ? all : all.filter((r) => r.updatedAt > since);
  }

  async get(entityId: EntityId): Promise<SyncRecord | null> {
    return this.records.get(String(entityId)) ?? null;
  }
}

/** Maps each entity kind to its store collection so the engine can load bodies. */
const KIND_TO_COLLECTION: Readonly<Record<EntityKind, string>> = {
  capture_item: Collections.captureItems,
  task: Collections.tasks,
  source_record: Collections.sourceRecords,
  review: Collections.reviews,
  reminder: Collections.reminders,
  target: Collections.targets,
  consistency_evidence: Collections.evidence,
  habit: Collections.habits,
  habit_checkin: Collections.habitCheckins,
  routine: Collections.routines,
  problem: Collections.problems,
  project: Collections.projects,
  event: Collections.calendarEvents,
  account: Collections.accounts,
  transaction: Collections.transactions,
  import_batch: Collections.importBatches,
  note: Collections.notes,
  goal: Collections.goals,
  milestone: Collections.milestones,
  health_log: Collections.healthLogs,
  study_subject: Collections.studySubjects,
  study_session: Collections.studySessions,
  opportunity: Collections.opportunities,
  journal_entry: Collections.journalEntries,
  content_item: Collections.contentItems,
  venture: Collections.ventures,
  inventory_item: Collections.inventoryItems,
  focus_session: Collections.focusSessions,
  direction_item: Collections.directionItems,
  profile: Collections.profiles,
  admission: Collections.admissions,
  funding_source: Collections.fundingSources,
  education_program: Collections.educationPrograms,
  resume_profile: Collections.resumeProfiles,
  security_event: Collections.securityEvents,
};

export interface SyncOutcome {
  /** Records successfully pushed to the target. */
  readonly pushed: number;
  /** Remote records adopted locally (downloaded / fast-forwarded). */
  readonly pulled: number;
  /** Records that diverged on both sides and need manual review. */
  readonly conflicted: number;
  /** Mutations skipped (strict-local, or whose record was missing). */
  readonly skipped: number;
  readonly lastSyncAt: string;
}

/** Persisted sync watermark, surfaced in Runtime Status. */
export interface SyncMeta {
  readonly lastSyncAt: string;
}

const SYNC_META_KEY = 'sync_meta';

/** Read the last successful sync timestamp, or null if never synced. Pure read. */
export async function lastSyncAt(store: LocalStore): Promise<string | null> {
  const meta = await store.get<SyncMeta>(Collections.syncMeta, SYNC_META_KEY);
  return meta?.lastSyncAt ?? null;
}

/** How the owner chose to resolve a sync conflict. */
export type ConflictChoice = 'keep_local' | 'accept_remote';

/**
 * Drains the mutation queue against a SyncTarget. Honours the conflict rule
 * (never overwrite) and the privacy rule (strict-local never leaves).
 */
export class SyncEngine {
  constructor(
    private readonly store: LocalStore,
    private readonly target: SyncTarget,
    private readonly clock: Clock,
  ) {}

  async sync(): Promise<SyncOutcome> {
    const since = await lastSyncAt(this.store);
    const all = await this.store.list<Mutation>(Collections.mutations);
    const queued = all.filter((m) => m.syncState === 'local_pending' || m.syncState === 'failed');

    let pushed = 0;
    let conflicted = 0;
    let skipped = 0;

    for (const m of queued) {
      // Privacy gate: strict-local / device-secret never crosses the seam.
      if (isLocalOnly(m.privacyClass)) {
        skipped++;
        continue;
      }

      const collection = KIND_TO_COLLECTION[m.kind];
      const record = await this.store.get<RecordEnvelope>(collection, String(m.entityId));
      if (!record) {
        skipped++;
        continue;
      }

      const remote = await this.target.head(m.entityId);
      if (remote) {
        const decision = detectConflict(
          { localVersion: record.localVersion, baseVersion: m.baseVersion },
          remote,
        );
        if (decision.kind === 'conflict_review') {
          const review: ConflictReview = {
            id: `cf_${String(m.entityId)}_${decision.remoteVersion}`,
            entityId: m.entityId,
            localVersion: decision.localVersion,
            remoteVersion: decision.remoteVersion,
            baseVersion: m.baseVersion,
            createdAt: this.clock.nowIso(),
            resolved: false,
          };
          await this.store.put(Collections.conflicts, review.id, review);
          await this.store.put(Collections.mutations, String(m.localId), {
            ...m,
            syncState: 'conflict_review',
          });
          conflicted++;
          continue;
        }
      }

      // No conflict: push the body, mark synced, and advance the shared base
      // version so subsequent edits are measured against what the remote knows.
      await this.target.push({
        entityId: m.entityId,
        kind: m.kind,
        version: record.localVersion,
        privacyClass: m.privacyClass,
        updatedAt: record.updatedAt,
        body: record,
      });
      await this.store.put(Collections.mutations, String(m.localId), { ...m, syncState: 'synced' });
      await this.store.put(collection, String(record.id), {
        ...record,
        baseVersion: record.localVersion,
      });
      pushed++;
    }

    // Pull pass: adopt remote changes so every device converges. This is what
    // lets a second device (desktop, phone) receive what the others created.
    const pulled = await this.pull(since);

    const at = this.clock.nowIso();
    await this.store.put<SyncMeta>(Collections.syncMeta, SYNC_META_KEY, { lastSyncAt: at });
    return { pushed, pulled, conflicted, skipped, lastSyncAt: at };
  }

  /**
   * Download remote records changed since the watermark and merge them in.
   * Safe-by-default: a remote record is adopted only when the local copy is
   * absent or clean (no un-pushed local edits) and the remote is strictly
   * newer. Locally-diverged records are left untouched — the push pass already
   * raised a ConflictReview for them, so we never clobber unsynced local work.
   */
  private async pull(since: string | null): Promise<number> {
    let pulled = 0;
    let remoteRecords: readonly SyncRecord[];
    try {
      remoteRecords = await this.target.pull(since);
    } catch {
      // A target without pull support (or a transient error) simply yields no
      // downloads; push already succeeded. Keep sync resilient.
      return 0;
    }

    for (const r of remoteRecords) {
      if (isLocalOnly(r.privacyClass)) continue;
      if (!r.body || typeof r.body !== 'object') continue;
      const collection = KIND_TO_COLLECTION[r.kind];
      if (!collection) continue;

      const local = await this.store.get<RecordEnvelope>(collection, String(r.entityId));
      if (!local) {
        // Brand-new on this device — adopt it as a clean, synced record.
        await this.store.put(collection, String(r.entityId), {
          ...(r.body as RecordEnvelope),
          baseVersion: r.version,
          localVersion: r.version,
        });
        pulled++;
        continue;
      }

      const clean = local.localVersion === local.baseVersion;
      if (clean && r.version > local.localVersion) {
        // Fast-forward: no local divergence, remote is newer.
        await this.store.put(collection, String(r.entityId), {
          ...(r.body as RecordEnvelope),
          baseVersion: r.version,
          localVersion: r.version,
        });
        pulled++;
      }
      // else: local is ahead or diverged — leave it; conflicts are handled by
      // the push pass / resolveConflict, never by silent overwrite.
    }
    return pulled;
  }

  /**
   * Resolve a recorded conflict without data loss.
   *  - `keep_local`   lifts the local record above the remote version and
   *                   re-queues it, so the next sync pushes it as a clean win.
   *  - `accept_remote` adopts the remote body locally and marks it synced.
   * The review is marked resolved either way. No-ops on unknown/resolved ids.
   */
  async resolveConflict(conflictId: string, choice: ConflictChoice): Promise<void> {
    const review = await this.store.get<ConflictReview>(Collections.conflicts, conflictId);
    if (!review || review.resolved) return;

    const mutations = await this.store.list<Mutation>(Collections.mutations);
    const mutation = mutations.find(
      (m) => String(m.entityId) === String(review.entityId) && m.syncState === 'conflict_review',
    );
    const collection = mutation ? KIND_TO_COLLECTION[mutation.kind] : undefined;

    if (choice === 'accept_remote') {
      const remote = await this.target.get(review.entityId);
      if (remote && collection && remote.body && typeof remote.body === 'object') {
        await this.store.put(collection, String(review.entityId), {
          ...(remote.body as RecordEnvelope),
          baseVersion: remote.version,
          localVersion: remote.version,
        });
      }
      if (mutation) {
        await this.store.put(Collections.mutations, String(mutation.localId), {
          ...mutation,
          baseVersion: review.remoteVersion,
          syncState: 'synced',
        });
      }
    } else {
      if (collection) {
        const local = await this.store.get<RecordEnvelope>(collection, String(review.entityId));
        if (local) {
          await this.store.put(collection, String(review.entityId), {
            ...local,
            localVersion: Math.max(local.localVersion, review.remoteVersion) + 1,
            baseVersion: review.remoteVersion,
            updatedAt: this.clock.nowIso(),
          });
        }
      }
      if (mutation) {
        await this.store.put(Collections.mutations, String(mutation.localId), {
          ...mutation,
          baseVersion: review.remoteVersion,
          syncState: 'local_pending',
        });
      }
    }

    await this.store.put(Collections.conflicts, conflictId, { ...review, resolved: true });
  }
}
